import { ChevronRightIcon } from "@heroicons/react/outline";
import { ensureTrailingSlash } from "@utils/ensureTrailingSlash";
import { capitalizeFirstLetter, removeLeadingSlash } from "@utils/utils";
import { useStaticQuery, graphql } from "gatsby";
import { Link } from "gatsby-link";
import { JsonLd } from "gatsby-plugin-next-seo";
import React, { useMemo } from "react";
import { BreadcrumbList, ListItem, WithContext } from "schema-dts";
import { DeepNonNullable } from "ts-essentials";
import cn from "classnames";
import { usePageContext } from "./GlobalContextProvider";

export type BreadcrumbParentType = {
  title: string;
  url: string;
};

type Props = {
  title: string;
  capitalizeTitle?: boolean;
  parents?: BreadcrumbParentType[];
  hubPath?: string;
  hubTitle?: string;
  sticky?: boolean;
};

const Breadcrumb = ({
  title,
  capitalizeTitle = true,
  parents = [],
  hubPath,
  hubTitle,
  sticky = false,
}: Props) => {
  const { locale, genericTexts } = usePageContext();
  const { site } = useStaticQuery<DeepNonNullable<Queries.SEODataQuery>>(
    graphql`
      query SEOData {
        site {
          siteMetadata {
            title
            description
            author
            siteUrl
          }
        }
      }
    `
  );

  const jsonLDBreadcrumb = useMemo((): WithContext<BreadcrumbList> => {
    const hubItem: ListItem | null = hubPath
      ? {
          "@type": "ListItem",
          position: 1,
          name: capitalizeFirstLetter(hubTitle),
          item: `${site.siteMetadata.siteUrl}/${ensureTrailingSlash(
            removeLeadingSlash(hubPath)
          )}`,
        }
      : null;
    const parentListItems: ListItem[] = parents?.map(
      (parent, index): ListItem => ({
        "@type": "ListItem",
        position: hubPath ? index + 2 : index + 1,
        name: capitalizeFirstLetter(parent.title),
        item: `${site.siteMetadata.siteUrl}/${ensureTrailingSlash(
          removeLeadingSlash(parent.url)
        )}`,
      })
    );
    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        ...(hubItem ? [hubItem] : []),
        ...parentListItems,
        {
          "@type": "ListItem",
          position: hubPath ? parents.length + 2 : parents.length + 1,
          name: capitalizeFirstLetter(title),
        },
      ],
    };
  }, [parents, title]);

  return (
    <nav
      className={cn("z-20 mt-4 mb-6 flex", {
        "top-[96px] md:sticky": sticky,
      })}
      aria-label="Breadcrumb"
    >
      {sticky && (
        <div className="full-width top-0 right-0 left-0 hidden h-full min-w-full bg-accuRankerPurple-900 md:absolute md:block" />
      )}
      <JsonLd defer={false} json={jsonLDBreadcrumb} />
      <ol className="relative z-10 m-0 flex list-none flex-wrap items-center gap-x-4 capitalize">
        <li>
          <Link
            to={locale !== "en" ? `/${locale}/` : "/"}
            className="text-accuRankerOrange-500"
            style={{ textTransform: "capitalize" }}
          >
            {genericTexts?.homepageLabel || "Homepage"}
          </Link>
        </li>
        {hubPath && hubTitle && (
          <li>
            <div className="flex items-center gap-3 text-gray-400">
              <ChevronRightIcon
                className="h-5 w-5 flex-shrink-0"
                aria-hidden="true"
              />
              <Link to={hubPath} className="text-accuRankerOrange-500">
                {hubTitle}
              </Link>
            </div>
          </li>
        )}
        {parents &&
          parents.map(parent => {
            let url = parent.url;
            if (!url.endsWith("/")) {
              url = `${url}/`;
            }
            if (!url.startsWith("/")) {
              url = `/${url}`;
            }
            return (
              <li key={parent.url}>
                <div className="flex items-center gap-3 capitalize text-gray-400">
                  <ChevronRightIcon
                    className="h-5 w-5 flex-shrink-0"
                    aria-hidden="true"
                  />
                  <Link to={url} className="text-accuRankerOrange-500">
                    {parent.title}
                  </Link>
                </div>
              </li>
            );
          })}
        <li>
          <div className="flex items-center gap-3 text-gray-400">
            <ChevronRightIcon
              className="h-5 w-5 flex-shrink-0"
              aria-hidden="true"
            />
            <span
              className={cn({
                "normal-case": !capitalizeTitle,
              })}
            >
              {title}
            </span>
          </div>
        </li>
      </ol>
    </nav>
  );
};
export default Breadcrumb;
